import { ref, type Ref } from 'vue';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { apiScope, getMsal } from '@/auth/msal';
import type { AdminEventDto } from '@/api/types';

// SignalR connection state surfaced to the UI. The transport layer fans these out from the
// underlying HubConnection lifecycle. AppShell renders an indicator chip from this ref.
export type RealtimeStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'offline';

export const connectionId: Ref<string | null> = ref(null);
export const status: Ref<RealtimeStatus> = ref('idle');

type Handler = (e: AdminEventDto) => void;

// Map of event kind → set of handlers. Stores subscribe per-kind so the dispatcher remains
// O(handlers-for-this-kind) rather than scanning every registered handler on every event.
const handlers = new Map<string, Set<Handler>>();
// Handlers that fire for every event regardless of kind. Used by AppShell debug logging /
// future global hooks. Empty in the current build.
const wildcardHandlers = new Set<Handler>();
// Fires after a successful (re)connect. Stores use this to force-refresh after the gap so
// missed events don't leave stale state.
type RecoverHandler = () => void | Promise<void>;
const recoverHandlers = new Set<RecoverHandler>();

let connection: HubConnection | null = null;
let startPromise: Promise<void> | null = null;

function dispatch(e: AdminEventDto) {
  const targeted = handlers.get(e.kind);
  if (targeted) {
    for (const h of targeted) {
      try { h(e); } catch (err) { console.warn('[realtime] handler threw', e.kind, err); }
    }
  }
  for (const h of wildcardHandlers) {
    try { h(e); } catch (err) { console.warn('[realtime] wildcard handler threw', e.kind, err); }
  }
}

async function fetchAccessToken(): Promise<string> {
  const msal = getMsal();
  const account = msal.getAllAccounts()[0];
  if (!account) throw new Error('Not signed in');
  try {
    const result = await msal.acquireTokenSilent({ account, scopes: [apiScope()] });
    return result.accessToken;
  } catch {
    const result = await msal.acquireTokenPopup({ scopes: [apiScope()] });
    return result.accessToken;
  }
}

function build(): HubConnection {
  const hub = new HubConnectionBuilder()
    .withUrl('/api/admin/events', {
      accessTokenFactory: fetchAccessToken,
    })
    .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 30_000])
    .configureLogging(LogLevel.Warning)
    .build();

  hub.on('adminEvent', (e: AdminEventDto) => dispatch(e));

  hub.onreconnecting(() => {
    status.value = 'reconnecting';
    connectionId.value = null;
  });
  hub.onreconnected((id) => {
    connectionId.value = id ?? null;
    status.value = 'live';
    // Replay missed window: every subscribed store force-refreshes. Cheaper than maintaining
    // a server-side replay buffer; sufficient for the operator count we expect.
    for (const r of recoverHandlers) {
      try { void r(); } catch (err) { console.warn('[realtime] recover handler threw', err); }
    }
  });
  hub.onclose(() => {
    status.value = 'offline';
    connectionId.value = null;
  });

  return hub;
}

// Idempotent — repeated calls return the same in-flight start promise. AppShell calls this
// on mount; the api client also imports the connectionId ref so its header attaches as
// soon as the handshake lands.
export async function startRealtime(): Promise<void> {
  if (startPromise) return startPromise;
  status.value = 'connecting';
  connection = build();
  startPromise = (async () => {
    try {
      await connection!.start();
      connectionId.value = connection!.connectionId ?? null;
      status.value = 'live';
    } catch (err) {
      console.warn('[realtime] initial connect failed; SPA stays in pull-only mode', err);
      status.value = 'offline';
      // Allow a retry the next time something asks (e.g. operator clicks reload).
      startPromise = null;
      throw err;
    }
  })();
  return startPromise;
}

export async function stopRealtime(): Promise<void> {
  const c = connection;
  connection = null;
  startPromise = null;
  status.value = 'idle';
  connectionId.value = null;
  if (c && c.state !== HubConnectionState.Disconnected) {
    try { await c.stop(); } catch { /* swallow */ }
  }
}

// Subscribe to events of a single kind. Returns a disposer so callers (Pinia stores
// instantiated for a SPA's lifetime in practice) can opt out — handy for tests.
export function on(kind: string, handler: Handler): () => void {
  let set = handlers.get(kind);
  if (!set) {
    set = new Set();
    handlers.set(kind, set);
  }
  set.add(handler);
  return () => { set!.delete(handler); };
}

export function onAny(handler: Handler): () => void {
  wildcardHandlers.add(handler);
  return () => { wildcardHandlers.delete(handler); };
}

export function onRecover(handler: RecoverHandler): () => void {
  recoverHandlers.add(handler);
  return () => { recoverHandlers.delete(handler); };
}
